// Count Set CSV export (TC-PCV2-EXP-006).
//
// Root cause being fixed: the Count Set "Export CSV" action called
// tabulator.download('csv'), which dumps whatever product-line grid columns
// happen to be visible — NOT the linked Physical Documents. The regression
// case requires EXACTLY these 7 columns, one row per linked Physical Document:
//
//   1) Document ID    2) Reference   3) Document Date   4) Total Quantity
//   5) Status         6) Order Status                   7) Created By
//
// The Count Set form already loads its members via
//   GET documents/get_pcount_document_details?id=<count_set_id>
// which serializes each linked Physical Document through DocumentSerializer.
// Every one of the 7 fields already exists on that serialized row:
//
//   Document ID    -> document_no
//   Reference      -> reference        (DocumentSerializer#reference => reference_1)
//   Document Date  -> document_date    (DocumentSerializer formats 'YYYY-MM-DD')
//   Total Quantity -> total_quantity   (per-member stored total_quantity)
//   Status         -> status           (DocumentSerializer#status, already a label)
//   Order Status   -> order_status     (DocumentSerializer#order_status, already a label)
//   Created By     -> created_by        (DocumentSerializer#created_by => user_name)
//
// This module builds the CSV purely from that already-loaded member data so it
// is unit-testable without a browser. Status / Order Status are passed through
// when already human-readable; if a raw single-letter code slips through it is
// mapped to the SAME label DocumentSerializer uses, so the CSV matches the UI.

// Human-readable Status labels — identical to DocumentSerializer#status.
const STATUS_LABELS = {
  S: 'Saved',
  P: 'Posted',
  V: 'Voided',
  C: 'Confirmed',
  O: 'Ongoing Posting',
};

// Human-readable Order Status labels — identical to the generic (non PCN/BAR)
// branch of DocumentSerializer#order_status. Linked Count Set members are
// Physical Counts (OTHPCN), so U => 'UnFreeze' and F => 'Frozen'.
const ORDER_STATUS_LABELS = {
  U: 'UnFreeze',
  P: 'Printed',
  F: 'Frozen',
  D: 'Done',
  O: 'Open Delivery',
  G: 'Goods Issued',
  S: 'Paid',
  C: 'Completed',
  I: 'In-Transit',
};

// The exact ordered column headers required by TC-PCV2-EXP-006.
export const COUNT_SET_EXPORT_COLUMNS = [
  'Document ID',
  'Reference',
  'Document Date',
  'Total Quantity',
  'Status',
  'Order Status',
  'Created By',
];

function mapStatus(value) {
  if (value === null || value === undefined || value === '') return 'No Status';
  // Already a human-readable label (serialized) -> pass through unchanged.
  if (Object.values(STATUS_LABELS).includes(value)) return value;
  return STATUS_LABELS[value] || 'No Status';
}

function mapOrderStatus(value) {
  if (value === null || value === undefined || value === '') return 'No Order Status';
  if (Object.values(ORDER_STATUS_LABELS).includes(value)) return value;
  return ORDER_STATUS_LABELS[value] || 'No Order Status';
}

// Normalizes one serialized linked Physical Document into the 7 CSV cells
// (as plain, unescaped strings) in the required column order.
export function countSetMemberToRow(member) {
  const m = member || {};
  const totalQty = m.total_quantity;
  return [
    m.document_no == null ? '' : String(m.document_no),
    m.reference == null ? '' : String(m.reference),
    m.document_date == null ? '' : String(m.document_date),
    totalQty == null || totalQty === '' ? '' : String(totalQty),
    mapStatus(m.status),
    mapOrderStatus(m.order_status),
    m.created_by == null ? '' : String(m.created_by),
  ];
}

// RFC-4180 style CSV field escaping: wrap in double quotes and double any inner
// quote when the value contains a comma, quote, CR or LF.
function escapeCsvField(value) {
  const str = value == null ? '' : String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Builds the full CSV text (header row + one row per linked Physical Document)
// from the already-loaded Count Set member array. Rows preserve input order.
export function buildCountSetExportCsv(members) {
  const list = Array.isArray(members) ? members : [];
  const lines = [COUNT_SET_EXPORT_COLUMNS.map(escapeCsvField).join(',')];
  list.forEach((member) => {
    lines.push(countSetMemberToRow(member).map(escapeCsvField).join(','));
  });
  // Trailing newline keeps parity with tabulator's CSV output and plays nicely
  // with spreadsheet importers.
  return `${lines.join('\n')}\n`;
}
